import { useState, useEffect } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, BarChart, Bar, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import { TrendingUp, ChevronDown, ChevronUp, MessageSquare, Calendar, Filter, Target, Sparkles, RefreshCw, Users } from 'lucide-react';
import { normalizeNote, moyenneNormalisee } from '../utils/normalizeNote';
import CarnetNotesFormateur from '../components/CarnetNotesFormateur';
import CarnetNotesTuteur from '../components/CarnetNotesTuteur';

const Progression = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [moyenneClasse, setMoyenneClasse] = useState(null);
  const [competences, setCompetences] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState(user.user_id);
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedCategorie, setSelectedCategorie] = useState('all');
  const [selectedItem, setSelectedItem] = useState('all');
  const [expandedNote, setExpandedNote] = useState(null);
  const [customEpreuves, setCustomEpreuves] = useState([]);
  const [analyseIA, setAnalyseIA] = useState(null);
  const [analyseDate, setAnalyseDate] = useState(null);
  const [analyseLoading, setAnalyseLoading] = useState(false);
  const [analyseCached, setAnalyseCached] = useState(false);
  
  // Filtres de période
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // Structure hiérarchique des catégories (même que Notation.js)
  const categoriesStructure = {
    'Briefing': ['BRIEFING VNM', 'BRIEFING Engins tractés', 'BRIEFING ESH', 'BRIEFING PNM', 'BRIEFING JET À BRAS'],
    'Encadrement': ['VNM 2 jet tuteur', 'VNM 2 jet autonomie', 'VNM 4 jet tuteur', 'VNM 4 jet autonomie', 'Engins tractés', 'ESH', 'PNM', 'JET À BRAS'],
    'Sécurité': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau', 'Engins tractés'],
    'Pédagogie': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Aisance': ['VNM', 'Jet à bras', 'PNM', 'Engins tractés', 'Bateau'],
    'Procédure d\'intervention': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Animation': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Engins tractés'],
    'Compétences Générales': [
      'Entretien rangement du matériel',
      'Implication générale'
    ],
    'Prérequis': ['Parcours jet à bras', 'Parcours jet à selle', 'Parcours engins tractés'],
    'Épreuves Formatives': [
      'Réglementation',
      'Public',
      'Physio',
      'Anatomie',
      'Météo',
      'Message de secours',
      ...customEpreuves
    ]
  };

  const [stagiaireReady, setStagiaireReady] = useState(user.role === 'stagiaire');

  useEffect(() => {
    if (user.role !== 'stagiaire') {
      loadStagiaires();
    }
  }, []);

  useEffect(() => {
    if (user.role === 'stagiaire' || stagiaireReady) {
      loadNotes();
      loadCompetences();
      loadCustomEpreuves();
      loadAnalyseIA();
      loadMoyenneClasse();
    }
  }, [selectedStagiaire, stagiaireReady]);

  // Réinitialiser l'item quand la catégorie change
  useEffect(() => {
    setSelectedItem('all');
  }, [selectedCategorie]);

  const loadCompetences = async () => {
    try {
      const response = await axios.get(`/competences/stagiaire/${selectedStagiaire}`);
      setCompetences(response.data || []);
    } catch (error) {
    }
  };

  const loadAnalyseIA = async () => {
    try {
      const res = await axios.get(`/analyse-competences/${selectedStagiaire}`);
      if (res.data?.analyse) {
        setAnalyseIA(res.data.analyse);
        setAnalyseDate(res.data.created_at);
        setAnalyseCached(true);
      } else {
        setAnalyseIA(null);
        setAnalyseDate(null);
        setAnalyseCached(false);
      }
    } catch { setAnalyseIA(null); }
  };

  const generateAnalyseIA = async () => {
    setAnalyseLoading(true);
    try {
      const res = await axios.post(`/analyse-competences/${selectedStagiaire}`);
      setAnalyseIA(res.data.analyse);
      setAnalyseDate(res.data.created_at);
      setAnalyseCached(false);
    } catch (err) {
      if (err.response?.status === 429) {
        alert('Une analyse a déjà été générée cette semaine. Revenez la semaine prochaine.');
      } else {
        alert('Erreur lors de l\'analyse. Réessayez.');
      }
    }
    setAnalyseLoading(false);
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      if (response.data.length > 0) {
        const ids = response.data.map(s => s.user_id);
        if (!ids.includes(selectedStagiaire)) {
          setSelectedStagiaire(response.data[0].user_id);
        }
        setStagiaireReady(true);
      }
    } catch (error) {
    }
  };

  const loadMoyenneClasse = async () => {
    try {
      const response = await axios.get('/notes/moyenne-classe');
      setMoyenneClasse(response.data.moyenne_classe);
    } catch (error) {
    }
  };

  const loadCustomEpreuves = async () => {
    try {
      const response = await axios.get('/epreuves-formatives');
      setCustomEpreuves(response.data.map(e => e.sujet));
    } catch (error) {
    }
  };

  const loadNotes = async () => {
    try {
      const stagiaireId = user.role === 'stagiaire' ? user.user_id : selectedStagiaire;
      const [notesRes, statsRes] = await Promise.all([
        axios.get(`/notes/stagiaire/${stagiaireId}`),
        axios.get(`/notes/stats/${stagiaireId}`)
      ]);
      setNotes(notesRes.data);
      setStats(statsRes.data);
    } catch (error) {
    }
  };

  const getCompetenceLevel = (note) => {
    if (note >= 0 && note <= 4) return { label: 'Non compétent', color: '#EF4444', bg: 'bg-red-50' };
    if (note >= 5 && note <= 7) return { label: "En cours d'acquisition", color: '#F59E0B', bg: 'bg-orange-50' };
    if (note >= 8 && note <= 10) return { label: 'Acquis', color: '#8DC63F', bg: 'bg-green-50' };
    return { label: 'Non évalué', color: '#94A3B8', bg: 'bg-slate-50' };
  };

  // Filtrer les notes par période
  const notesFiltered = notes.filter(n => {
    const noteDate = new Date(n.date);
    if (dateDebut && new Date(dateDebut) > noteDate) return false;
    if (dateFin && new Date(dateFin) < noteDate) return false;
    return true;
  });

  // Items disponibles selon la catégorie sélectionnée
  const availableItems = selectedCategorie !== 'all' 
    ? categoriesStructure[selectedCategorie] || []
    : [];

  // Préparer les données pour le graphique par jour
  const prepareChartData = () => {
    let filteredNotes = notesFiltered;
    
    // Filtrer par catégorie
    if (selectedCategorie !== 'all') {
      filteredNotes = filteredNotes.filter(n => n.categorie === selectedCategorie);
    }
    
    // Filtrer par item
    if (selectedItem !== 'all') {
      filteredNotes = filteredNotes.filter(n => n.item === selectedItem);
    }

    // Grouper par jour et calculer la moyenne normalisée
    const grouped = filteredNotes.reduce((acc, note) => {
      const date = note.date.split('T')[0];
      if (!acc[date]) acc[date] = { vals: [], details: [] };
      acc[date].vals.push(normalizeNote(note));
      acc[date].details.push({
        categorie: note.categorie || 'Autre',
        item: note.item || '',
        note: normalizeNote(note),
        commentaire: note.commentaire || ''
      });
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        fullDate: date,
        note: parseFloat((data.vals.reduce((a, b) => a + b, 0) / data.vals.length).toFixed(1)),
        count: data.vals.length,
        details: data.details
      }))
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  };

  // Tooltip personnalisé
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const note = data.note;
      const level = getCompetenceLevel(note);
      const details = data.details || [];
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-[#0F172A] text-sm">{data.date}</p>
          <p className="text-lg font-bold mb-1" style={{ color: level.color }}>{note}/10 <span className="text-xs font-normal text-slate-500">{level.label}</span></p>
          {details.length > 0 && (
            <div className="border-t border-slate-100 pt-1.5 mt-1 space-y-1">
              {details.map((d, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-600">
                      <span className="font-medium text-slate-700">{d.categorie}</span>
                      {d.item && <span className="text-slate-400"> / {d.item}</span>}
                    </span>
                    <span className="text-xs font-bold">{d.note.toFixed(0)}/10</span>
                  </div>
                  {d.commentaire && (
                    <p className="text-[10px] text-slate-400 leading-tight truncate">{d.commentaire.substring(0, 60)}{d.commentaire.length > 60 ? '...' : ''}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Filtrer les notes pour le carnet
  const getFilteredNotes = () => {
    let filtered = notes;
    if (selectedCategorie !== 'all') {
      filtered = filtered.filter(n => n.categorie === selectedCategorie);
    }
    if (selectedItem !== 'all') {
      filtered = filtered.filter(n => n.item === selectedItem);
    }
    return filtered;
  };
  
  const filteredNotes = getFilteredNotes();

  return (
    <div className="space-y-6" data-testid="progression-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Progression</h1>
          <p className="text-slate-600">Notes et compétences acquises</p>
        </div>
        {user.role !== 'stagiaire' && stagiaires.length > 0 && (
          <select
            value={selectedStagiaire}
            onChange={(e) => setSelectedStagiaire(e.target.value)}
            className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="select-stagiaire"
          >
            {stagiaires.map((s) => (
              <option key={s.user_id} value={s.user_id}>
                {s.user_info?.prenom} {s.user_info?.nom}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-[#8DC63F]" data-testid="moyenne-generale">
          <div className="flex items-center space-x-4">
            <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
              <TrendingUp className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats?.moyenne_generale || 0}/10</p>
              <p className="text-sm text-slate-600">Moyenne Générale</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-blue-500" data-testid="moyenne-classe">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-500/10 p-3 rounded-lg">
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{moyenneClasse != null ? `${moyenneClasse}/10` : '-'}</p>
              <p className="text-sm text-slate-600">Moyenne Classe</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="total-notes">
          <p className="text-3xl font-display font-bold text-[#0F172A] mb-1">{stats?.total_notes || 0}</p>
          <p className="text-sm text-slate-600">Évaluations</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="categories">
          <p className="text-3xl font-display font-bold text-[#0F172A] mb-1">
            {stats?.par_categorie ? Object.keys(stats.par_categorie).length : 0}
          </p>
          <p className="text-sm text-slate-600">Catégories évaluées</p>
        </div>
      </div>

      {/* Graphique Radar - Compétences acquises en centre de formation */}
      {notes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5 text-[#8DC63F]" />
            <h2 className="text-xl font-display font-semibold text-[#0F172A]">Compétences acquises en centre de formation</h2>
          </div>
          
          {/* Légende des zones améliorée */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm justify-center">
            <span className="flex items-center gap-2 px-3 py-1 bg-red-50 rounded-full border border-red-200">
              <span className="w-3 h-3 bg-red-400 rounded-full"></span> 
              <span className="text-red-700 font-medium">Non compétent (0-4)</span>
            </span>
            <span className="flex items-center gap-2 px-3 py-1 bg-orange-50 rounded-full border border-orange-200">
              <span className="w-3 h-3 bg-orange-400 rounded-full"></span> 
              <span className="text-orange-700 font-medium">En cours (5-7)</span>
            </span>
            <span className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full border border-green-200">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span> 
              <span className="text-green-700 font-medium">Acquis (8-10)</span>
            </span>
          </div>
          
          <ResponsiveContainer width="100%" height={450}>
            <RadarChart 
              cx="50%" 
              cy="50%" 
              outerRadius="75%" 
              data={(() => {
                // Grouper les notes par catégorie et calculer la moyenne normalisée /10
                const groupedByCategorie = {};
                notes.forEach(note => {
                  const cat = note.categorie || 'Autre';
                  if (!groupedByCategorie[cat]) {
                    groupedByCategorie[cat] = { total: 0, count: 0 };
                  }
                  groupedByCategorie[cat].total += normalizeNote(note);
                  groupedByCategorie[cat].count += 1;
                });
                
                return Object.entries(groupedByCategorie).map(([cat, data]) => ({
                  categorie: cat.length > 15 ? cat.substring(0, 15) + '…' : cat,
                  fullName: cat,
                  moyenne: data.count > 0 ? Math.round((data.total / data.count) * 10) / 10 : 0,
                  fullMark: 10
                }));
              })()}
            >
              <PolarGrid gridType="polygon" stroke="#e2e8f0" />
              <PolarAngleAxis 
                dataKey="categorie" 
                tick={{ fontSize: 11, fill: '#334155', fontWeight: 500 }}
                tickLine={false}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 10]} 
                tickCount={6}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
              />
              <Radar
                name="Moyenne par catégorie"
                dataKey="moyenne"
                stroke="#22c55e"
                strokeWidth={2}
                fill="url(#colorGradientNotes)"
                fillOpacity={0.6}
              />
              <defs>
                <linearGradient id="colorGradientNotes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.8}/>
                  <stop offset="100%" stopColor="#8DC63F" stopOpacity={0.3}/>
                </linearGradient>
              </defs>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
                formatter={(value) => {
                  const moyenne = parseFloat(value).toFixed(1);
                  if (value >= 8) return [<span className="text-green-600 font-semibold">Acquis ({moyenne}/10)</span>, 'Niveau'];
                  if (value >= 5) return [<span className="text-orange-600 font-semibold">En cours ({moyenne}/10)</span>, 'Niveau'];
                  return [<span className="text-red-600 font-semibold">Non compétent ({moyenne}/10)</span>, 'Niveau'];
                }}
                labelFormatter={(label) => <span className="font-semibold text-slate-700">{label}</span>}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={() => <span className="text-slate-600 text-sm">Moyenne des notes par catégorie</span>}
              />
            </RadarChart>
          </ResponsiveContainer>
          
          {/* Détail par catégorie */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(
              notes.reduce((acc, note) => {
                const cat = note.categorie || 'Autre';
                if (!acc[cat]) acc[cat] = { notes: [], total: 0 };
                const normalized = normalizeNote(note);
                acc[cat].notes.push(normalized);
                acc[cat].total += normalized;
                return acc;
              }, {})
            ).map(([cat, data]) => {
              const moyenne = data.notes.length > 0 ? (data.total / data.notes.length).toFixed(1) : 0;
              const bgColor = moyenne >= 8 ? 'bg-green-50 border-green-200' : moyenne >= 5 ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200';
              const textColor = moyenne >= 8 ? 'text-green-600' : moyenne >= 5 ? 'text-orange-600' : 'text-red-600';
              return (
                <div key={cat} className={`p-3 rounded-lg border ${bgColor}`}>
                  <p className="font-medium text-slate-700 truncate text-sm" title={cat}>{cat}</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className={`text-xl font-bold ${textColor}`}>{moyenne}</span>
                    <span className="text-slate-400 text-xs">/10</span>
                    <span className="text-slate-400 text-xs ml-auto">({data.notes.length} notes)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Histogramme - Compétences acquises en entreprise */}
      {competences.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5 text-blue-500" />
            <h2 className="text-xl font-display font-semibold text-[#0F172A]">Compétences acquises en entreprise</h2>
          </div>
          
          {/* Légende */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm justify-center">
            <span className="flex items-center gap-2 px-3 py-1 bg-red-50 rounded-full border border-red-200">
              <span className="w-3 h-3 bg-red-400 rounded-full"></span> 
              <span className="text-red-700 font-medium">Non compétent (0-1)</span>
            </span>
            <span className="flex items-center gap-2 px-3 py-1 bg-orange-50 rounded-full border border-orange-200">
              <span className="w-3 h-3 bg-orange-400 rounded-full"></span> 
              <span className="text-orange-700 font-medium">En cours (1-2)</span>
            </span>
            <span className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full border border-green-200">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span> 
              <span className="text-green-700 font-medium">Compétent (2-3)</span>
            </span>
          </div>
          
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={(() => {
                const groupedByCategorie = {};
                competences.forEach(comp => {
                  const cat = comp.categorie || 'Autre';
                  if (!groupedByCategorie[cat]) {
                    groupedByCategorie[cat] = { total: 0, count: 0 };
                  }
                  let value = 0;
                  if (comp.statut === 'competent') value = 3;
                  else if (comp.statut === 'en_cours') value = 2;
                  else if (comp.statut === 'non_competent') value = 1;
                  groupedByCategorie[cat].total += value;
                  groupedByCategorie[cat].count += 1;
                });
                return Object.entries(groupedByCategorie).map(([cat, data]) => ({
                  categorie: cat,
                  niveau: data.count > 0 ? Math.round((data.total / data.count) * 100) / 100 : 0,
                }));
              })()}
              margin={{ top: 10, right: 20, left: 0, bottom: 60 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="categorie"
                tick={{ fontSize: 11, fill: '#334155' }}
                angle={-30}
                textAnchor="end"
                interval={0}
                height={70}
              />
              <YAxis domain={[0, 3]} ticks={[0, 1, 2, 3]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                formatter={(value) => {
                  const niveau = parseFloat(value).toFixed(2);
                  if (value >= 2) return [<span className="text-green-600 font-semibold">Compétent ({niveau}/3)</span>, 'Niveau'];
                  if (value >= 1) return [<span className="text-orange-600 font-semibold">En cours ({niveau}/3)</span>, 'Niveau'];
                  return [<span className="text-red-600 font-semibold">Non compétent ({niveau}/3)</span>, 'Niveau'];
                }}
                labelFormatter={(label) => <span className="font-semibold text-slate-700">{label}</span>}
              />
              <Bar dataKey="niveau" radius={[6, 6, 0, 0]} maxBarSize={50}>
                {(() => {
                  const groupedByCategorie = {};
                  competences.forEach(comp => {
                    const cat = comp.categorie || 'Autre';
                    if (!groupedByCategorie[cat]) groupedByCategorie[cat] = { total: 0, count: 0 };
                    let value = 0;
                    if (comp.statut === 'competent') value = 3;
                    else if (comp.statut === 'en_cours') value = 2;
                    else if (comp.statut === 'non_competent') value = 1;
                    groupedByCategorie[cat].total += value;
                    groupedByCategorie[cat].count += 1;
                  });
                  return Object.entries(groupedByCategorie).map(([cat, data], idx) => {
                    const niveau = data.count > 0 ? data.total / data.count : 0;
                    const color = niveau >= 2 ? '#22c55e' : niveau >= 1 ? '#f59e0b' : '#ef4444';
                    return <Cell key={idx} fill={color} />;
                  });
                })()}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          
          {/* Détail par catégorie */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(
              competences.reduce((acc, comp) => {
                const cat = comp.categorie || 'Autre';
                if (!acc[cat]) acc[cat] = { competent: 0, en_cours: 0, non_competent: 0 };
                if (comp.statut === 'competent') acc[cat].competent++;
                else if (comp.statut === 'en_cours') acc[cat].en_cours++;
                else if (comp.statut === 'non_competent') acc[cat].non_competent++;
                return acc;
              }, {})
            ).map(([cat, counts]) => (
              <div key={cat} className="p-2 bg-slate-50 rounded text-xs">
                <p className="font-medium text-slate-700 truncate" title={cat}>{cat}</p>
                <div className="flex gap-1 mt-1">
                  <span className="text-green-600">{counts.competent}✓</span>
                  <span className="text-orange-500">{counts.en_cours}~</span>
                  <span className="text-red-500">{counts.non_competent}✗</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analyse IA des compétences */}
      {(competences.length > 0 || notes.length > 0) && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="analyse-ia-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-500" />
              <h2 className="text-xl font-display font-semibold text-[#0F172A]">Analyse IA de vos compétences</h2>
            </div>
            <button
              onClick={generateAnalyseIA}
              disabled={analyseLoading}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${analyseLoading ? 'bg-slate-200 text-slate-500 cursor-wait' : 'bg-violet-600 hover:bg-violet-700 text-white shadow-md hover:shadow-lg'}`}
              data-testid="btn-analyse-ia"
            >
              {analyseLoading ? (
                <><RefreshCw className="h-4 w-4 animate-spin" /> Analyse en cours...</>
              ) : analyseIA ? (
                <><RefreshCw className="h-4 w-4" /> Actualiser (1x/semaine)</>
              ) : (
                <><Sparkles className="h-4 w-4" /> Analyser mes compétences</>
              )}
            </button>
          </div>

          {analyseIA ? (
            <div>
              {analyseDate && (
                <p className="text-xs text-slate-400 mb-3">
                  Analyse du {new Date(analyseDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {analyseCached && ' (en cache)'}
                </p>
              )}
              <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed space-y-2"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    analyseIA
                      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-slate-900">$1</strong>')
                      .replace(/\n- /g, '\n<br/>&bull; ')
                      .replace(/\n\n/g, '<br/><br/>')
                      .replace(/\n/g, '<br/>')
                  )
                }}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-violet-300" />
              <p className="text-sm">Cliquez sur le bouton pour obtenir une analyse personnalisée de vos compétences</p>
              <p className="text-xs mt-1">L'analyse est générée par IA et mise en cache pendant 7 jours</p>
            </div>
          )}
        </div>
      )}

      {/* Filtres de période et catégorie/item */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-[#0F172A]">Filtres</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Période */}
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Du
            </label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="date-debut"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Au
            </label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="date-fin"
            />
          </div>
          {/* Catégorie */}
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500">Catégorie</label>
            <select
              value={selectedCategorie}
              onChange={(e) => {
                setSelectedCategorie(e.target.value);
                setSelectedItem('all');
              }}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-categorie"
            >
              <option value="all">Toutes les catégories</option>
              {Object.keys(categoriesStructure).map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          {/* Item/Article */}
          <div className="flex flex-col space-y-1">
            <label className="text-xs font-medium text-slate-500">
              Article {selectedCategorie !== 'all' && <span className="text-slate-400">({selectedCategorie})</span>}
            </label>
            <select
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-item"
              disabled={selectedCategorie === 'all'}
            >
              <option value="all">{selectedCategorie === 'all' ? 'Sélectionnez une catégorie' : 'Tous les articles'}</option>
              {availableItems.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Bouton reset */}
        {(dateDebut || dateFin || selectedCategorie !== 'all' || selectedItem !== 'all') && (
          <button 
            onClick={() => { setDateDebut(''); setDateFin(''); setSelectedCategorie('all'); setSelectedItem('all'); }}
            className="mt-3 text-xs text-[#8DC63F] hover:underline"
          >
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {/* Graphique avec zones de couleur */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-2">
          Évolution {selectedCategorie !== 'all' ? `- ${selectedCategorie}` : ''} {selectedItem !== 'all' ? `/ ${selectedItem}` : ''}
        </h2>
        <p className="text-sm text-slate-500 mb-4">Notes par jour avec zones de niveau</p>
        
        {prepareChartData().length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={prepareChartData()} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
              {/* Zones de fond colorées */}
              <ReferenceArea y1={0} y2={4} fill="#FEE2E2" fillOpacity={0.6} />
              <ReferenceArea y1={4} y2={7} fill="#FEF3C7" fillOpacity={0.6} />
              <ReferenceArea y1={7} y2={10} fill="#DCFCE7" fillOpacity={0.6} />
              
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="date" 
                style={{ fontSize: 12 }}
                tick={{ fill: '#64748B' }}
              />
              <YAxis 
                domain={[0, 10]} 
                ticks={[0, 2, 4, 5, 7, 8, 10]}
                style={{ fontSize: 12 }}
                tick={{ fill: '#64748B' }}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Lignes de référence pour les seuils */}
              <ReferenceLine y={4} stroke="#EF4444" strokeDasharray="5 5" label={{ value: '4', position: 'right', fill: '#EF4444', fontSize: 10 }} />
              <ReferenceLine y={7} stroke="#F59E0B" strokeDasharray="5 5" label={{ value: '7', position: 'right', fill: '#F59E0B', fontSize: 10 }} />
              
              {/* Ligne des notes avec points */}
              <Line 
                type="monotone" 
                dataKey="note" 
                stroke="#0F172A" 
                strokeWidth={2}
                dot={{ fill: '#0F172A', strokeWidth: 2, r: 5 }}
                activeDot={{ r: 8, fill: '#8DC63F' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-slate-500 text-center py-12">Aucune donnée disponible</p>
        )}

        {/* Légende */}
        <div className="flex flex-wrap justify-center gap-6 mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
            <span className="text-sm text-slate-600">0-4 : Non compétent</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
            <span className="text-sm text-slate-600">5-7 : En cours d'acquisition</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
            <span className="text-sm text-slate-600">8-10 : Acquis</span>
          </div>
        </div>
      </div>

      {/* Carnet de Notes - groupé par Support (cohérent avec toutes les vues) */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <CarnetNotesFormateur notes={filteredNotes} title="Carnet de Notes - Formateur" />
      </div>

      {/* Carnet de Notes Tuteur - évaluations compétences entreprise */}
      {competences.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <CarnetNotesTuteur evaluations={competences} title="Carnet de Notes - Tuteur" />
        </div>
      )}
    </div>
  );
};

export default Progression;
